import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import useSound from "use-sound";
import Roulette from "./strip/Roulette";
import { rouletteItemBgType, rouletteItemFrameType, roulettePinType } from "./RouletteConstants";
import { mergeProps } from "../../utils/mergeUtils";

const getTotalRarity = (items) => {
    return items.filter(item => !item.hidden)
        .reduce((partSum, item) => partSum + item.rarity, 0)
}

const categoryRewardsToList = (rewardNames) => {
    return rewardNames.split(",").map(rewardName => rewardName.trim());
}

const RouletteComponent = ({ rouletteData, spinDuration, soundSrc, volume, onWin, rouletteStyle }, ref) => {

    const [play] = useSound(soundSrc, { 
        volume: volume
    });

    const [currentIndex, setCurrentIndex] = useState();

    const rouletteRef = useRef();

    const rouletteCategories = useMemo(() => {
        if (!rouletteData) return [];

        return rouletteData.categoryEntries.filter(item => !item.hidden).map((item, categoryIndex) => {
            const rewardNames = categoryRewardsToList(item.rewardNames);

            if (item.font) {
                const link = document.createElement("link");
                link.href = item.font.url;
                link.rel = "stylesheet";
                document.head.appendChild(link);
            }

            return rewardNames.map((rewardName, itemIndex) => ({
                id: `${categoryIndex}${itemIndex}`,
                name: rewardName,
                probability: ((item.rarity / getTotalRarity(rouletteData.categoryEntries)) * 100 / rewardNames.length),
                cardStyle: {
                    size: item.backgroundSize,
                    radius: item.backgroundRadius,
                    ...(item.backgroundType === "COLOR" && {
                        color: {
                            color1: item.backgroundColor1,
                            color2: item.backgroundColor2,
                            angle: item.colorGradientAngle
                        }
                    }),
                    shadow: {
                        color: item.backgroundShadowColor,
                        horizontalOffset: item.backgroundShadowHorizontalOffset,
                        verticalOffset: item.backgroundShadowVerticalOffset,
                        blur: item.backgroundShadowBlur
                    },
                    ...(item.backgroundType === "IMAGE" && {
                        image: {
                            url: item.backgroundImage.url,
                            hueRotate: item.backgroundImageHue,
                            saturate: item.backgroundImageSaturation
                        }
                    })
                },
                borderStyle: {
                    size: item.frameSize,
                    radius: item.frameRadius,
                    width: item.frameOutlineWidth,
                    ...(item.frameType === "OUTLINE" && {
                        outline: {
                            color1: item.frameColor1,
                            color2: item.frameColor2,
                            gradientAngle: item.frameColorGradientAngle
                        }
                    }),
                    ...(item.frameType === "IMAGE" && {
                        image: {
                            url: item.frameImage.url,
                            hueRotate: item.frameImageHue,
                            saturate: item.frameImageSaturation
                        }
                    }),
                    shadow: {
                        color: item.frameShadowColor,
                        horizontalOffset: item.frameShadowHorizontalOffset,
                        verticalOffset: item.frameShadowVerticalOffset,
                        blur: item.frameShadowBlur
                    }
                },
                textStyle: {
                    font: item.font?.name,
                    color: item.textColor,
                    fontWeight: item.textStyle[0].selected ? item.textStyle[0].value : 'normal',
                    fontStyle: item.textStyle[1].selected ? item.textStyle[1].value : 'normal',
                    textDecoration: item.textStyle[2].selected ? item.textStyle[2].value : 'none',
                    textAlign: item.textAlignment,
                    textTransform: item.textTransformation,
                    fontSize: item.fontSize,
                    stroke: {
                        width: item.fontOutlineWidth,
                        color: item.textOutlineColor
                    },
                    shadow: {
                        color: item.textShadowColor,
                        horizontalOffset: item.textShadowHorizontalOffset,
                        verticalOffset: item.textShadowVerticalOffset,
                        blur: item.textShadowBlur
                    },
                    lineHeight: item.lineHeight,
                    letterSpacing: item.letterSpacing,
                    wordSpacing: item.wordSpacing,
                    transform: {
                        x: item.fontPositionX,
                        y: item.fontPositionY
                    },
                    fontBlockWidth: item.fontBlockWidth,
                    textOutlineBackgroundSize: item.textOutlineBackgroundSize,
                    textOutlineBackgroundColor: item.textOutlineBackgroundColor
                }
            }))
        }).flat();
    }, [rouletteData])

    const rouletteBackground = useMemo(() => {

        let data = rouletteData;
        if (rouletteStyle) {
            data = mergeProps(rouletteData, rouletteStyle);
        }

        if (!data) return {};
        
        if (data.backgroundType === rouletteItemBgType.COLOR) {
  
            return {
                radius: data.backgroundRadius,
                color: {
                    color1: data.backgroundColor1,
                    color2: data.backgroundColor2,
                    angle: data.backgroundColorAngle
                },
                shadow: {
                    color: data.backgroundShadowColor,
                    horizontalOffset: data.backgroundShadowHorizontalOffset,
                    verticalOffset: data.backgroundShadowVerticalOffset,
                    blur: data.backgroundShadowBlur
                }
            }
        }

        if (data.backgroundType === rouletteItemBgType.IMAGE) {
            
            return {
                radius: data.backgroundRadius,
                image: {
                    url: data.backgroundImage?.url,
                    hueRotate: data.backgroundImageHueRotate,
                    saturate: data.backgroundImageSaturate
                },
                shadow: {
                    color: data.backgroundShadowColor,
                    horizontalOffset: data.backgroundShadowHorizontalOffset,
                    verticalOffset: data.backgroundShadowVerticalOffset,
                    blur: data.backgroundShadowBlur
                }
            }
        }

        return {};
    }, [rouletteData, rouletteStyle])

    const rouletteBorder = useMemo(() => {

        let data = rouletteData;
        if (rouletteStyle) {
            data = mergeProps(rouletteData, rouletteStyle);
        }
        
        if (!data) return {};

        if (data.frameType === rouletteItemFrameType.OUTLINE) {

            return {
                color: {
                    width: data.frameOutlineWidth,
                    radius: data.frameRadius,
                    color1: data.frameColor1,
                    color2: data.frameColor2,
                    gradientAngle: data.frameColorGradientAngle,
                },
                shadow: {
                    color: data.frameShadowColor,
                    horizontalOffset: data.frameShadowHorizontalOffset,
                    verticalOffset: data.frameShadowVerticalOffset,
                    blur: data.frameShadowBlur
                }
            }
        }

        if (data.frameType === rouletteItemFrameType.IMAGE) {
            
            return {
                image: {
                    url: data.frameImage?.url,
                    hueRotate: data.frameImageHueRotate,
                    saturate: data.frameImageSaturate
                },
                shadow: {
                    color: data.frameShadowColor,
                    horizontalOffset: data.frameShadowHorizontalOffset,
                    verticalOffset: data.frameShadowVerticalOffset,
                    blur: data.frameShadowBlur
                }
            }
        }
    }, [rouletteData, rouletteStyle])

    const roulettePin = useMemo(() => {

        let data = rouletteData;
        if (rouletteStyle) {
            data = mergeProps(rouletteData, rouletteStyle);
        }

        if (!data) return {};

        if (data.pinType === roulettePinType.VECTOR) {
            return {
                vector: {
                    url: data.pinImage?.url,
                    stroke: data.pinOutlineColor,
                    strokeWidth: data.pinOutlineWidth,
                    color1: data.pinColor1,
                    color2: data.pinColor2,
                    gradientAngle: data.pinColorGradientAngle
                },
                shadow: {
                    color: data.pinShadowColor,
                    horizontalOffset: data.pinShadowHorizontalOffset,
                    verticalOffset: data.pinShadowVerticalOffset,
                    blur: data.pinShadowBlur
                }
            }
        }

        if (data.pinType === roulettePinType.IMAGE) {
            return {
                image: {
                    url: data.pinImage?.url,
                    hueRotate: data.pinImageHue,
                    saturate: data.pinImageSaturation
                },
                shadow: {
                    color: data.pinShadowColor,
                    horizontalOffset: data.pinShadowHorizontalOffset,
                    verticalOffset: data.pinShadowVerticalOffset,
                    blur: data.pinShadowBlur
                }
            }
        }

        return {};
    }, [rouletteData, rouletteStyle])

    const spin = () => {
        rouletteRef?.current?.spin();
    }

    useEffect(() => {
        play();
    }, [currentIndex])

    useImperativeHandle(ref, () => ({
        spin
    }), []);

    return (
        <Roulette 
            ref={rouletteRef}
            items={rouletteCategories} 
            spinDuration={spinDuration}
            onWin={onWin}
            onCurrentItem={({ index }) => setCurrentIndex(index)}
            backgroundStyle={rouletteBackground}
            borderStyle={rouletteBorder}
            pinStyle={roulettePin}
        />
    )
}

export default memo(forwardRef(RouletteComponent));